import {ChangeDetectorRef, Component, NgZone, OnInit, ViewEncapsulation} from "@angular/core";

import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {ComponentBase} from "../base/ComponentBase";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {PipelineService} from "../services/pipeline/pipeline.service";
import {IMetaData, ISelection} from "../api/data-model";
import {PipelineSaveEvent, PIPELINE_API_STATUS} from "./PipelineSaveEvent";
import {ClientConfiguration} from "../services/configuration/client.config";
import {HttpClient} from "@angular/common/http";
import {AudioState} from "../constants";

@Component({
    selector: "metadata",
    templateUrl: "./metadata.editor.component.html",
    styleUrls: ["./pipeline.component.scss"],
    encapsulation: ViewEncapsulation.None
})
export class MetadataEditorComponent extends ComponentBase implements OnInit {

    public activateModal: boolean = false;
    public status:string = "";
    public metadatas = new Array<string>();
    public _selection: ISelection  = this.pipelineService.currentSourceSelection;
    public saveInProgress: boolean = false;
    public creditDescription = "";
    public creditAttribution = "";
    public originalMetadata: IMetaData = null;

    public genreValues: string[] = [
        "Realistic Fiction",
        "Science Fiction",
        "General Fiction",
        "Historical Fiction",
        "Drama",
        "Mystery",
        "Fantasy",
        "Poetry",
        "Folklore/Fables/Myths",
        "Biography/Autobiography",
        "Narrative Nonfiction",
        "Nonfiction"];

    // For now content restrictions are fixed but will be requested from server
    // ALL_CONTENT exists but is commented since default content restriction is
    // not for exclusion
    public exclusions = [
        // { id: 1, name: "ALL_CONTENT" },
        { id: 2, name: "NOT_INTERNATIONAL_LICENSED" }
    ];

    public authorRepresentation = [
        {id: 1, name: "Middle Eastern"},
        {id: 2, name: "Asian/Pacific Islander"},
        {id: 3, name: "Black/African"},
        {id: 4, name: "Latinx"},
        {id: 5, name: "Native American"},
        {id: 6, name: "White"},
        {id: 7, name: "Racially Ambiguous"},
        {id: 8, name: "Multiracial"},
        {id: 9, name: "People with Disabilities"}
    ];

    public characterRepresentation = [
        {id: 1, name: "Middle Eastern"},
        {id: 2, name: "Asian/Pacific Islander"},
        {id: 3, name: "Black/African"},
        {id: 4, name: "Latinx"},
        {id: 5, name: "Native American"},
        {id: 6, name: "White"},
        {id: 7, name: "Racially Ambiguous"},
        {id: 8, name: "Multiracial"},
        {id: 9, name: "People with Disabilities"},
        {id: 10, name: "Animals"},
        {id: 11, name: "Monsters"}
    ];

    public protagonistGender = [
        "Girl/Woman",
        "Boy/Man",
        "Nonbinary"
    ];

    public settings = [
        "Rural",
        "Suburban",
        "Urban",
        "Fantasy Setting",
        "Historical Setting"
    ];

    public contentAreas = [
        {id: 1, name: "Biology"},
        {id: 2, name: "Chemistry"},
        {id: 3, name: "Civics & Government"},
        {id: 4, name: "Earth & Space Science"},
        {id: 5, name: "Economics"},
        {id: 6, name: "English Language Arts"},
        {id: 7, name: "Geography"},
        {id: 8, name: "Life Science"},
        {id: 9, name: "Mathematics"},
        {id: 10, name: "Music"},
        {id: 11, name: "Performing Arts"},
        {id: 12, name: "Physics"},
        {id: 13, name: "Social Studies"},
        {id: 14, name: "Technology & Engineering"},
        {id: 15, name: "U.S. History"},
        {id: 16, name: "Visual Arts"},
        {id: 17, name: "World History"},
        {id: 18, name: "World Languages"}
    ];

    public contentTags = [
        {id: 1, name: "Acceptance/Fitting In"},
        {id: 2, name: "Adventure"},
        {id: 3, name: "Advocacy"},
        {id: 4, name: "Amphibians and Reptiles"},
        {id: 5, name: "Animals"},
        {id: 6, name: "Art"},
        {id: 7, name: "Asian American History and Identity"},
        {id: 8, name: "Beaches"},
        {id: 9, name: "Being Part of a Community"},
        {id: 10, name: "Biracial & Multiracial Identity"},
        {id: 11, name: "Birds"},
        {id: 12, name: "Black American History and Identity"},
        {id: 13, name: "Bravery"},
        {id: 14, name: "Bullying"},
        {id: 15, name: "Citizenship"},
        {id: 16, name: "Civics, Civic Life"},
        {id: 17, name: "Civil Rights"},
        {id: 18, name: "Civil War"},
        {id: 19, name: "Coming of Age"},
        {id: 20, name: "Conservation/Sustainability"},
        {id: 21, name: "Death/Loss"},
        {id: 22, name: "Deserts"},
        {id: 23, name: "Dinosaurs"},
        {id: 24, name: "Disability"},
        {id: 25, name: "Ecosystems & Habitats"},
        {id: 26, name: "Electricity"},
        {id: 27, name: "Energy"},
        {id: 28, name: "Environment"},
        {id: 29, name: "Family & Home Life"},
        {id: 30, name: "Forests"},
        {id: 31, name: "Friendship"},
        {id: 32, name: "Ghost Story"},
        {id: 33, name: "Good Citizenship"},
        {id: 34, name: "Government"},
        {id: 35, name: "Grief & Loss"},
        {id: 36, name: "Growth Mindset"},
        {id: 37, name: "Health, Healthy Habits, Nutrition"},
        {id: 38, name: "Hispanic/Latino/Latinx History and Identity"},
        {id: 39, name: "Holidays"},
        {id: 40, name: "Holocaust"},
        {id: 41, name: "Homelessness"},
        {id: 42, name: "Human Body Systems"},
        {id: 43, name: "Humor"},
        {id: 44, name: "Identifying Emotions"},
        {id: 45, name: "Immigration"},
        {id: 46, name: "Jewish History and Identity"},
        {id: 47, name: "Justice, Freedom & Equality"},
        {id: 48, name: "Kindness, Respect for Others"},
        {id: 49, name: "Landforms"},
        {id: 50, name: "Law"},
        {id: 51, name: "Leadership"},
        {id: 52, name: "Life Cycles"},
        {id: 53, name: "Light & Sound"},
        {id: 54, name: "Loneliness"},
        {id: 55, name: "Love and Romance"},
        {id: 56, name: "Magnets"},
        {id: 57, name: "Making Choices"},
        {id: 58, name: "Making Friends"},
        {id: 59, name: "Mammals"},
        {id: 60, name: "Mapping/Maps"},
        {id: 61, name: "Marine Life"},
        {id: 62, name: "Math"},
        {id: 63, name: "Mindfulness"},
        {id: 64, name: "Money"},
        {id: 65, name: "Moving"},
        {id: 66, name: "Multilingual Identity"},
        {id: 67, name: "Music"},
        {id: 68, name: "Muslim History and Identity"},
        {id: 69, name: "Native American History and Identity"},
        {id: 70, name: "Nature"},
        {id: 71, name: "Oceans"},
        {id: 72, name: "Outer Space"},
        {id: 73, name: "Overcoming Obstacles"},
        {id: 74, name: "Performing Arts"},
        {id: 75, name: "Pets"},
        {id: 76, name: "Prejudice & Discrimination"},
        {id: 77, name: "Race"},
        {id: 78, name: "Racism"},
        {id: 79, name: "Rainforests"},
        {id: 80, name: "Real Life Heroes"},
        {id: 81, name: "Religion"},
        {id: 82, name: "Resilience"},
        {id: 83, name: "School Life, Learning"},
        {id: 84, name: "Self-Confidence"},
        {id: 85, name: "Slavery and Abolition"},
        {id: 86, name: "Social Change & Revolution"},
        {id: 87, name: "Sports"},
        {id: 88, name: "States of Matter"},
        {id: 89, name: "Technology"},
        {id: 90, name: "Travel"},
        {id: 91, name: "US States"},
        {id: 92, name: "Visual Arts"},
        {id: 93, name: "Volunteering"},
        {id: 94, name: "War"},
        {id: 95, name: "Water Cycles"},
        {id: 96, name: "Weather"},
        {id: 97, name: "Westward Expansion"},
        {id: 98, name: "Women's History"},
        {id: 99, name: "World Cultures"},
        {id: 100, name: "World War II"}
    ];

    public dropdownDefault = "---Select---";

    constructor(
        private http: HttpClient,
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private logService: BugsnagLoggingService,
        private pipelineService: PipelineService,
        private config: ClientConfiguration,
        private ref: ChangeDetectorRef
    ) {
        super("MetadataEditorComponent");
    }

    ngOnInit(): void {
        this.originalMetadata = JSON.parse(JSON.stringify(this.selection.metaData));
    }

    get selection(): ISelection {
        return this.pipelineService.currentSourceSelection;
    }

    public save(): void {
        let doSave = true;
        if (this.flagAudioTextMismatch()) {
            if (confirm("Intro text has audio scripted or recorded which now may not match edited text (it will be marked as needing re-recording). Are you sure?")
                !== true) {
                doSave = false;
            }
        }
        if (doSave) {
            this.originalMetadata = JSON.parse(JSON.stringify(this.selection.metaData));
            this.saveMetadata();
        } else { // not saving, so revert to original metadata.  Kind of overkill, since only intro text has audio?
            this.selection.metaData = JSON.parse(JSON.stringify(this.originalMetadata));
            this.ref.detectChanges();
        }
    }

    public saveMetadata(): void {
        this.saveInProgress = true;
        if (!this.selection.metaData.seriesName) {
            delete this.selection.metaData.seriesName;
            delete this.selection.metaData.orderWithinSeries;
        }
        if (this.creditAttribution || this.creditDescription) {
            if (!this.selection.metaData.credits || (this.selection.metaData.credits.length === 0)) {
                this.selection.metaData.credits = [];
                this.selection.metaData.credits.push({type: "OTHER", location: 0, attribution: "", description: ""});
            }
            let selectionCredit = this.selection.metaData.credits.find((credit) => {return credit.location === 0;});
            if (selectionCredit) {
                selectionCredit.attribution = this.creditAttribution;
                selectionCredit.description = this.creditDescription;
            }
            this.selection.metaData.credits[0].attribution = this.creditAttribution;
            this.selection.metaData.credits[0].description = this.creditDescription;
        }
        // if the credit has been deleted, make that update also
        if (!this.creditAttribution.trim() && !this.creditDescription.trim()) {
            if (this.selection.metaData.credits && this.selection.metaData.credits.length > 0) {
                this.selection.metaData.credits = this.selection.metaData.credits.filter(
                    (credit) => credit.location !== 0 );
            }
        }
        // TODO: should refactor this to pipelineService
        this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.start));
        this.pipelineService.saveSourceContent().subscribe(result => {
            this.logService.log("saved uninflated");
            this.saveInProgress = false;
            this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.finish,
                this.pipelineService.currentSourceSelection));
        });
    }

    private flagAudioTextMismatch(): boolean {
        let flagMismatch = false;
        if (this.selection.metaData.introAudio && (this.selection.metaData.introText !== this.originalMetadata.introText)) {
            flagMismatch = true;
            if (this.selection.metaData.audioState !== AudioState.UNRECORDED) {
                this.selection.metaData.audioState = AudioState.RERECORD;
            }
        }
        return flagMismatch;
    }

    public getImageSrc(img: string): string {
        return img ? this.config.libraryImageUrl(img) : "";
    }

    counter(length: number) {
        return new Array(length);
    }

    public makeActive():void {
        if (this.selection.metaData.credits && (this.selection.metaData.credits.length > 0)) {
            let selectionCredit = this.selection.metaData.credits.find((credit) => {return credit.location === 0;});
            this.creditAttribution = selectionCredit.attribution;
            this.creditDescription = selectionCredit.description;
        }

    }

    public onATOSGradeChange(): void {
        if (this.selection.metaData.ATOSGradeEquivalent === 'NP' && this.selection.metaData.poetryLevel === undefined) {
            this.selection.metaData.poetryLevel = {
                fountasPinnellLevel: "",
                ATOSGradeEquivalent: "",
                lexileLevel: ""
            }
        }
    }

    public makeInactive():void {

    }
}

